import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { uniq, filter } from 'underscore';
import jsonData from '../../signals/jsonData';
import appState from '../../signals/appState';
import { exhibitorKeyOf } from '../../tools/boothExhibitors';
import useRecord from '../../hooks/useRecord';
import translation from '../../signals/translations';
import useData from '../../hooks/useData';
import splitMultiValue from '../../tools/splitMultiValue';
import IconClose from '../../icons/IconClose';
import IconHeart from '../../icons/IconHeart';
import IconHeartFilled from '../../icons/IconHeartFilled';


const ExhibitorList = () => {
	const { exhibitorInfo, currentLanguage } = jsonData.value;
	const { ExhibitorList, exhibitorSection } = appState.value;

	const { toggleFavourite } = useData();
	const [searchTerm, setSearchTerm] = useState('');
	const [currentLetter, setCurrentLetter] = useState('#');
	const { makeRecord } = useRecord();

	const closePanel = () => {
		appState.value = {
			...appState.value,
			ExhibitorList: false,
			MyFavourites: false,
			SearchPanel: false,
		};
	}

	useEffect(() => {
		if (exhibitorSection) setCurrentLetter('#');
	}, [exhibitorSection]);

	const exhibitorsBySection = useMemo(() => {
		const key = '區域';
		const exhibitorsBySection = exhibitorInfo.reduce((acc, exhibitor) => {
			// 分類欄位可以填多個（用分號隔開），每一個都要登記進去 ——
			// 這樣同一家公司在每個分類底下都找得到，而資料上只有一筆。
			// 廠商為了多分類把整列複製好幾份，正是「全部廠商」與搜尋結果重複的來源。
			splitMultiValue(exhibitor[key]).forEach((section) => {
				if (!acc[section]) acc[section] = [];
				acc[section].push(exhibitor);
			});
			return acc;
		}, {});
		return exhibitorsBySection;
	}, [exhibitorInfo]);

	const exhibitorsCurrentSection = useMemo(() => {
		if (exhibitorSection === 'all') return exhibitorInfo;
		return exhibitorsBySection[exhibitorSection] || [];
	}, [exhibitorInfo, exhibitorSection, exhibitorsBySection]);

	const firstLetters = useMemo(() => {
		const key = '首字母';
		const uniqueFirstLetters = uniq(exhibitorsCurrentSection.map(exhibitor => exhibitor[key]));

		uniqueFirstLetters.sort((a, b) => {
			return a.localeCompare(b, currentLanguage.code);
		});

		uniqueFirstLetters.unshift('#');

		if (uniqueFirstLetters.includes('')) {
			uniqueFirstLetters.splice(uniqueFirstLetters.indexOf(''), 1);
		}

		return uniqueFirstLetters;
	}, [currentLanguage.code, exhibitorsCurrentSection]);

	const exhibitorsByLetter = useMemo(() => {
		if (currentLetter === '#') return exhibitorsCurrentSection;
		const key = '首字母';
		const exhibitorsByLetter = filter(exhibitorsCurrentSection, exhibitor => exhibitor[key] === currentLetter);
		return exhibitorsByLetter;
	}, [currentLetter, exhibitorsCurrentSection]);

	const filteredExhibitorInfo = useMemo(() => {
		if (!searchTerm) return exhibitorsByLetter;
		if (searchTerm) return exhibitorsByLetter.filter((exhibitor) => {
			const term = searchTerm.toLowerCase();
			return exhibitor['廠商名稱'].toLowerCase().includes(term) || exhibitor['攤位號碼'].toLowerCase().includes(term);
		});
	}, [exhibitorsByLetter, searchTerm]);

	const toggleFavouriteExhibitor = useCallback((exhibitor) => {
		toggleFavourite(exhibitor);
	}, [toggleFavourite]);

	// 選的是「哪一家」（序號），攤位號碼只拿來標地圖——同號碼多家時才分得出點的是誰。
	const chooseExhibitor = (exhibitor, floor) => {
		appState.value = {
			...appState.value,
			ExhibitorInfo: true,
			activeFloor: floor !== undefined ? floor : appState.value.activeFloor,
			selectedBooth: exhibitor['攤位號碼'],
			selectedExhibitor: exhibitorKeyOf(exhibitor),
		};
	};

	const recordExhibitorClicked = useCallback((booth) => {
		makeRecord({
			Action: 'T',
			Booth: booth,
		});
	}, [makeRecord]);

	if (!ExhibitorList) return null;

	return (
		<div id='ExhibitorList' className={`ui-panel booth ${ExhibitorList ? 'active' : ''}`}>
			<div className='panel-heading'>{exhibitorSection === 'all' ? (translation('leftPanel.exhibitorList.allExhibitors')) : exhibitorSection}</div>
			<div className='panel-body'>
				<div className='flex-controls'>
					<div className='search'>
						<input type='text' placeholder={translation('leftPanel.exhibitorList.search')} value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value) }} />
					</div>
				</div>

				<div className='flex-body'>
					<div className='alphabets'>
						{firstLetters.map((letter, index) => {
							return (
								<div className={`alphabet ${currentLetter === letter ? 'active' : ''}`} key={`alphabet${index}`} onClick={() => { setCurrentLetter(letter) }}>{letter}</div>
							)
						})}
					</div>

					<div className='exhibitors'>
						{filteredExhibitorInfo.map((exhibitor, index) => {
							const exhibitorName = exhibitor['廠商名稱'];
							const booth = exhibitor['攤位號碼'];

							return (
								<div className='exhibitor' key={`exhibitorLinkTableRow${index}`}>
									<div className='name' onClick={() => { chooseExhibitor(exhibitor, exhibitor.floor); recordExhibitorClicked(booth); }}>{exhibitorName}</div>
									<div className='booth-number' onClick={() => { chooseExhibitor(exhibitor, exhibitor.floor); recordExhibitorClicked(booth); }}>{booth}</div>
									<div className='icon' onClick={() => { toggleFavouriteExhibitor(exhibitor) }}>
										{exhibitor.favourite ? <IconHeartFilled /> : <IconHeart />}
									</div>
								</div>
							)
						})}
					</div>
				</div>
			</div>

			<IconClose onClick={closePanel} />
		</div>
	)
}

export default ExhibitorList;